import {
  computed,
  readonly,
  ref,
  shallowReactive,
  type ComputedRef,
  type DeepReadonly,
  type Ref,
} from "vue";

/** An element ref value that may be a plain element or a Vue/Kendo ref-like object exposing `$el`. */
export type RegisterableElement =
  | HTMLElement
  | { $el?: HTMLElement | null }
  | null;

const resolveRegisteredElement = (
  value: RegisterableElement,
): HTMLElement | null => {
  if (value instanceof HTMLElement) return value;
  const componentElement = value && "$el" in value ? value.$el : null;
  return componentElement instanceof HTMLElement ? componentElement : null;
};

/** Functions and state returned by {@link useActiveIdRegistry}. */
export type UseActiveIdRegistryReturn<Id> = {
  /** The currently active id, or `null` when nothing is active. Read-only; use `activate`/`deactivate` to change it. */
  activeId: DeepReadonly<Ref<Id | null>>;
  /** `true` while any id is active. */
  isActive: ComputedRef<boolean>;
  /** The registered element for the active id, or `null`. */
  activeElement: ComputedRef<HTMLElement | null>;
  /** Registers (or unregisters, when `el` is `null`) the element for `id`. */
  register: (id: Id, el: RegisterableElement) => void;
  /** Returns the currently registered element for `id`, if any. */
  resolve: (id: Id) => HTMLElement | null;
  /** Marks `id` as active. */
  activate: (id: Id) => void;
  /** Clears the active id. */
  deactivate: () => void;
  /** Returns whether `id` is the currently active id. */
  isIdActive: (id: Id) => boolean;
};

/**
 * Tracks which single id, out of a rendered list of items (grid rows, tabs,
 * action buttons, etc.), is currently "active," alongside a registry
 * resolving each id to its mounted DOM element.
 *
 * This helper is useful for shared-instance patterns where one popup/menu instance
 * is reused across a list while a single item remains active at a time.
 *
 * @returns Active-id state, an element registry, and activation helpers.
 */
export const useActiveIdRegistry = <
  Id = string | number,
>(): UseActiveIdRegistryReturn<Id> => {
  const registry = shallowReactive(new Map<Id, HTMLElement>());
  const activeId = ref<Id | null>(null) as Ref<Id | null>;

  const register = (id: Id, el: RegisterableElement) => {
    const element = resolveRegisteredElement(el);
    if (element) {
      registry.set(id, element);
    } else {
      registry.delete(id);
    }
  };

  const resolve = (id: Id) => registry.get(id) ?? null;

  const activate = (id: Id) => {
    activeId.value = id;
  };

  const deactivate = () => {
    activeId.value = null;
  };

  const isIdActive = (id: Id) => activeId.value === id;

  return {
    activeId: readonly(activeId),
    isActive: computed(() => activeId.value !== null),
    activeElement: computed(() =>
      activeId.value !== null ? resolve(activeId.value) : null,
    ),
    register,
    resolve,
    activate,
    deactivate,
    isIdActive,
  };
};
